import React from "react";
import {
  TIME_COMPONENTS,
  VALUE_CLASSES,
  VALUE_CLASS_META,
} from "../../utils/whatif/whatIfNormalize";
import {
  convertMinutes,
  formatDurationShort,
  timeUnitShort,
} from "../../utils/whatif/whatIfFormat";

// Durations are held in minutes but typed in whatever unit the page is set to,
// so the input converts on the way in and out rather than forcing the user to
// think in minutes while every figure beside it reads in hours.
//
// An emptied field stays empty rather than snapping to "0", which would fight
// the user mid-edit. The arithmetic treats "" as 0 anyway, so the draft stays
// consistent while the box is blank.
const toInputValue = (minutes, unit) => {
  if (minutes === "" || minutes == null) return "";
  const converted = convertMinutes(minutes, unit);
  if (converted == null) return "";
  return String(Math.round(converted * 1000) / 1000);
};

const fromInputValue = (value, unit) => {
  if (value === "") return "";
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return unit === "hours" ? parsed * 60 : parsed;
};

const ClassPicker = ({ value, onChange }) => (
  <div className="wif-class-picker" role="group" aria-label="Value classification">
    {VALUE_CLASSES.map((key) => {
      const meta = VALUE_CLASS_META[key];
      const active = value === key;

      return (
        <button
          key={key}
          type="button"
          className={`wif-class-btn wif-class-btn--${key}${active ? " is-active" : ""}`}
          aria-pressed={active}
          title={`${meta.full} — ${meta.hint}`}
          onClick={() => onChange(key)}
        >
          {meta.label}
        </button>
      );
    })}
  </div>
);

const TimeField = ({ component, minutes, original, unit, onChange }) => {
  // Numeric compare so a blank box (which the draft reads as 0) does not count
  // as a change against an original of 0.
  const changed = Number(minutes || 0) !== Number(original || 0);

  return (
    <label className={`wif-time-field${changed ? " is-changed" : ""}`}>
      <span className="wif-time-label">{component.label}</span>
      <span className="wif-time-input">
        <input
          type="number"
          className="form-input"
          min="0"
          step="0.01"
          value={toInputValue(minutes, unit)}
          onChange={(event) => onChange(fromInputValue(event.target.value, unit))}
        />
        <span className="wif-slot-suffix">{timeUnitShort(unit)}</span>
      </span>
      {changed && (
        <span className="wif-time-was">
          was {formatDurationShort(original, unit)}
        </span>
      )}
    </label>
  );
};

const TaskCard = ({ entry, timeUnit, onTimeChange, onClassChange, onReset }) => {
  const { task, eliminated, editedTaskMinutes, originalTaskMinutes, changedCount } =
    entry;
  const delta = (eliminated ? 0 : editedTaskMinutes) - originalTaskMinutes;
  const direction = delta > 0 ? "up" : delta < 0 ? "down" : "flat";

  return (
    <div
      className={`wif-task-card${changedCount ? " wif-task-card--changed" : ""}${
        eliminated ? " wif-task-card--eliminated" : ""
      }`}
    >
      <div className="wif-task-card-head">
        <div className="wif-raci-task-heading">
         
          <span className="wif-raci-task-name">{task.taskName}</span>
        </div>

        <div className="wif-task-card-meta">
          <span className="wif-task-total">
            {formatDurationShort(eliminated ? 0 : editedTaskMinutes, timeUnit)}
          </span>
          {delta !== 0 && (
            <span className={`wif-delta wif-delta--${direction}`}>
              {delta >= 0 ? "+" : "−"}
              {formatDurationShort(Math.abs(delta), timeUnit)}
            </span>
          )}
          {changedCount > 0 && (
            <button type="button" className="wif-link-btn" onClick={onReset}>
              Reset task
            </button>
          )}
        </div>
      </div>

      <div className="wif-task-card-body">
        <div className="wif-time-fields">
          {TIME_COMPONENTS.map((component) => (
            <TimeField
              key={component.key}
              component={component}
              minutes={task.times[component.key]}
              original={task.originalTimes[component.key]}
              unit={timeUnit}
              onChange={(minutes) => onTimeChange(component.key, minutes)}
            />
          ))}
        </div>

        <div className="wif-task-class">
          <span className="wif-time-label">Classification</span>
          <ClassPicker value={task.classification} onChange={onClassChange} />
        </div>
      </div>

      {eliminated && (
        <p className="wif-task-eliminated-note">
          Non-value-adding — this task is eliminated, so its full{" "}
          {formatDurationShort(editedTaskMinutes, timeUnit)} and all labour
          staffed against it are removed from the to-be process.
        </p>
      )}
    </div>
  );
};

// The "Tasks" tab: each task's own duration and value classification.
const TasksTab = ({
  scenario,
  timeUnit,
  onTimeChange,
  onClassChange,
  onResetTask,
}) => (
  <>
    <p className="muted">
      Adjust each task's processing, waiting and rework time, and classify it as
      value-adding, business value-adding or non-value-adding. Classifying a task
      NVA eliminates it from the to-be process.
    </p>

    <div className="wif-scenario-tasks">
      {scenario.tasks.map((entry) => (
        <TaskCard
          key={entry.task.taskId}
          entry={entry}
          timeUnit={timeUnit}
          onTimeChange={(component, minutes) =>
            onTimeChange(entry.task.taskId, component, minutes)
          }
          onClassChange={(classification) =>
            onClassChange(entry.task.taskId, classification)
          }
          onReset={() => onResetTask(entry.task.taskId)}
        />
      ))}
    </div>
  </>
);

export default TasksTab;
